import { companyApi } from '@/services/company';
import { contractorsApi } from '@/services/contractors';

interface OnboardingStatusMap {
  profile: { route: 'profile'; step: 1 };
  teamMembers: { route: 'teamMembers'; step: 2 };
  configuration: { route: 'configuration'; step: 3 };
  elements: { route: 'elements'; step: 4 };
  complete: { route: 'complete'; step: 5 };
}

interface ContractorOnboardingStatusMap {
  businessInfo: { route: 'business-info'; step: 1 };
  classification: { route: 'classification'; step: 2 };
  contactInfo: { route: 'contact-info'; step: 3 };
  documents: { route: 'documents'; step: 4 };
  agreements: { route: 'agreements'; step: 5 };
  teamMembers: { route: 'add-users'; step: 6 };
  complete: { route: 'review'; step: 7 };
}

const STATUS_ROUTE_MAP: OnboardingStatusMap = {
  profile: { route: 'profile', step: 1 },
  teamMembers: { route: 'teamMembers', step: 2 },
  configuration: { route: 'configuration', step: 3 },
  elements: { route: 'elements', step: 4 },
  complete: { route: 'complete', step: 5 },
};

const CONTRACTOR_STATUS_ROUTE_MAP: ContractorOnboardingStatusMap = {
  businessInfo: { route: 'business-info', step: 1 },
  classification: { route: 'classification', step: 2 },
  contactInfo: { route: 'contact-info', step: 3 },
  documents: { route: 'documents', step: 4 },
  agreements: { route: 'agreements', step: 5 },
  teamMembers: { route: 'add-users', step: 6 },
  complete: { route: 'review', step: 7 },
};

export const useOnboardingNavigation = () => {
  const navigateToOnboarding = async (organizationId: string, accountType?: string) => {
    try {
      let onboardingStatus: any;
      let routeMap: any;
      let baseUrl = '';

      if (accountType === 'architect') {
        onboardingStatus = await contractorsApi.getArchitectOnboardingStatus(organizationId);
        routeMap = CONTRACTOR_STATUS_ROUTE_MAP;
        baseUrl = `/onboarding`;
      } else if (accountType === 'subcontractor') {
        onboardingStatus = await contractorsApi.getSubcontractorOnboardingStatus(organizationId);
        routeMap = CONTRACTOR_STATUS_ROUTE_MAP;
        baseUrl = `/onboarding`;
      } else {
        // Default to company onboarding
        onboardingStatus = await companyApi.getOnboardingStatus(organizationId);
        routeMap = STATUS_ROUTE_MAP;
        baseUrl = `/onboarding`;
      }

      console.log('Account type:', accountType);
      console.log('Onboarding status:', onboardingStatus);

      // PRIORITY 1: Check if complete === true
      // If onboarding is complete, return null to indicate no redirection to onboarding
      if (onboardingStatus.complete === true) {
        console.log('Onboarding is complete, no need to redirect to onboarding');
        return null; // This tells LoginPage to proceed to main app
      }

      // PRIORITY 2: If not complete, find the first incomplete step (first false value)
      const firstIncompleteStep = Object.entries(onboardingStatus).find(
        ([key, value]) => key !== 'overallProgress' && !value
      );

      console.log('First incomplete step:', firstIncompleteStep);

      if (firstIncompleteStep) {
        const [incompleteStepKey] = firstIncompleteStep;
        const statusConfig = routeMap[incompleteStepKey as keyof typeof routeMap];

        if (statusConfig) {
          const targetRoute = statusConfig.route;
          const typeParam = accountType ? `&type=${accountType}` : '';
          const targetUrl = `${baseUrl}/${targetRoute}?orgId=${organizationId}${typeParam}`;

          console.log('Redirecting to incomplete step:', targetUrl);
          return targetUrl; // Indicates redirection to onboarding step
        }
      }

      // If no incomplete step found and complete !== true, proceed to main app
      console.log('No incomplete steps found, proceeding to main app');
      return null;
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace',
      });
      return false; // Indicates error and fallback used
    }
  };

  const getFirstIncompleteStep = async (organizationId: string, accountType?: string) => {
    try {
      let onboardingStatus: any;
      let routeMap: any;

      if (accountType === 'architect') {
        onboardingStatus = await contractorsApi.getArchitectOnboardingStatus(organizationId);
        routeMap = CONTRACTOR_STATUS_ROUTE_MAP;
      } else if (accountType === 'subcontractor') {
        onboardingStatus = await contractorsApi.getSubcontractorOnboardingStatus(organizationId);
        routeMap = CONTRACTOR_STATUS_ROUTE_MAP;
      } else {
        onboardingStatus = await companyApi.getOnboardingStatus(organizationId);
        routeMap = STATUS_ROUTE_MAP;
      }

      // PRIORITY 1: Check if complete === true
      // If onboarding is complete, return null (no incomplete steps to navigate to)
      if (onboardingStatus.complete === true) {
        console.log('Onboarding is complete, no incomplete steps');
        return null;
      }

      // PRIORITY 2: If not complete, find the first incomplete step
      const firstIncompleteStep = Object.entries(onboardingStatus).find(
        ([key, value]) => key !== 'overallProgress' && !value
      );

      if (firstIncompleteStep) {
        const [incompleteStepKey] = firstIncompleteStep;
        const statusConfig = routeMap[incompleteStepKey as keyof typeof routeMap];
        return statusConfig
          ? { key: incompleteStepKey, ...statusConfig }
          : null;
      }

      return null; // All steps complete
    } catch (error) {
      console.error('Failed to get onboarding status:', error);
      return null;
    }
  };

  return {
    navigateToOnboarding,
    getFirstIncompleteStep,
    STATUS_ROUTE_MAP,
    CONTRACTOR_STATUS_ROUTE_MAP,
  };
};
